/**
 * Achievement evaluation. Pure functions over AppData + today; no React,
 * no side effects. The tracker component runs the evaluator after every
 * state change, then dispatches unlockAchievement for any newly-met IDs
 * that aren't already unlocked.
 *
 * Two achievements skip data-driven evaluation:
 *   - 'impulse-resist' is unlocked imperatively when the user picks
 *     "Skip it" in the Impulse Timer (we don't keep a counter for it).
 * Everything else flows from current AppData.
 */

import type { AppData } from '../data/types';
import { calculateDailyAllowance } from './budget';
import { addDays, startOfDay, toIsoDate } from './date';
import { getPayPeriod } from './payPeriod';

const TOOL_IDS = ['bill', 'math', 'impulse', 'pyf'] as const;

function consecutiveTransactionDays(
  data: AppData,
  today: Date,
  cap = 60,
): number {
  const dates = new Set(data.transactions.map((t) => t.date));
  if (dates.size === 0) return 0;
  let count = 0;
  const t = startOfDay(today);
  for (let i = 0; i < cap; i++) {
    if (dates.has(toIsoDate(addDays(t, -i)))) count++;
    else break;
  }
  return count;
}

function consecutiveUnderBudgetDays(
  data: AppData,
  today: Date,
  cap = 30,
): number {
  const r = calculateDailyAllowance(data, today);
  // Per-day discretionary slice — dailyBudget is now the income rate,
  // not a spending threshold, so derive the slice from the period's
  // discretionary pool.
  const allowance =
    r.period.totalDays > 0 ? r.discretionaryBudget / r.period.totalDays : 0;
  if (allowance <= 0) return 0;

  const spentByDay = new Map<string, number>();
  for (const t of data.transactions) {
    if (t.type !== 'expense') continue;
    spentByDay.set(t.date, (spentByDay.get(t.date) ?? 0) + t.amount);
  }

  let count = 0;
  const t0 = startOfDay(today);
  for (let i = 0; i < cap; i++) {
    const iso = toIsoDate(addDays(t0, -i));
    const spent = spentByDay.get(iso) ?? 0;
    if (spent <= allowance) count++;
    else break;
  }
  return count;
}

function previousPeriodWasUnderBudget(data: AppData, today: Date): boolean {
  const current = getPayPeriod(data.profile, today);
  // The day before the current period started is the last day of the previous one.
  const prevDay = addDays(current.start, -1);
  // calculateDailyAllowance on prevDay simulates being on the final day of the
  // previous period — spentThisPeriod is the period's full spend.
  const r = calculateDailyAllowance(data, prevDay);
  if (r.discretionaryBudget <= 0) return false;
  // Also require the previous period was actually completed (start before today).
  if (r.period.end >= startOfDay(today)) return false;
  return r.spentThisPeriod <= r.discretionaryBudget;
}

function daysSinceInstall(data: AppData, today: Date): number {
  const ts = data.installedAt ? Date.parse(data.installedAt) : Number.NaN;
  if (!Number.isFinite(ts)) return 0;
  const diffMs = today.getTime() - ts;
  return Math.floor(diffMs / (24 * 60 * 60 * 1000));
}

/** Evaluate every data-driven achievement and return the set that should be unlocked. */
export function evaluateAchievements(data: AppData, today: Date): Set<string> {
  const unlock = new Set<string>();

  // first-step
  if (data.transactions.length >= 1) unlock.add('first-step');

  // week-warrior
  if (consecutiveTransactionDays(data, today) >= 7) unlock.add('week-warrior');

  // budget-boss
  if (consecutiveUnderBudgetDays(data, today, 14) >= 7) unlock.add('budget-boss');

  // self-control
  if (consecutiveUnderBudgetDays(data, today, 21) >= 14) {
    unlock.add('self-control');
  }

  // month-master
  if (previousPeriodWasUnderBudget(data, today)) unlock.add('month-master');

  // savers-start
  if (data.goals.length >= 1) unlock.add('savers-start');

  // goal-getter
  if (data.goals.some((g) => g.targetAmount > 0 && g.currentAmount >= g.targetAmount)) {
    unlock.add('goal-getter');
  }

  // tool-time
  const used = new Set(data.toolsUsed ?? []);
  if (TOOL_IDS.every((id) => used.has(id))) unlock.add('tool-time');

  // centurion
  if (data.transactions.length >= 100) unlock.add('centurion');

  // consistent (30 days since install)
  if (daysSinceInstall(data, today) >= 30) unlock.add('consistent');

  // stellar (rated)
  if (data.rated) unlock.add('stellar');

  // 'impulse-resist' is intentionally NOT here — it's imperative-only.

  return unlock;
}

/** IDs of achievements that aren't yet unlocked but should be. */
export function pendingUnlocks(data: AppData, today: Date): string[] {
  const ids = evaluateAchievements(data, today);
  return data.achievements
    .filter((a) => !a.unlockedAt && ids.has(a.id))
    .map((a) => a.id);
}
